'use client';

import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const DSP_COLORS: Record<string, string> = {
  'Spotify': '#1DB954',
  'Apple Music': '#FC3C44',
  'YouTube Music': '#FF0000',
  'Amazon Music': '#00A8E1',
  'Tidal': '#00FFFF',
  'JioSaavn': '#2BC5B4',
  'Gaana': '#E72C30',
  'Wynk': '#7B2FBE',
};

const TERRITORY_COLORS = ['#7C3AED', '#2563EB', '#0891B2', '#059669', '#D97706', '#DC2626', '#7C3AED', '#DB2777'];

interface TerritoryData {
  territory: string;
  streams: number;
  earnings: number;
}

interface DspData {
  dsp: string;
  streams: number;
  earnings: number;
  releases: number;
}

interface AudienceDemographicsProps {
  territories: TerritoryData[];
  dspPerformance: DspData[];
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { territory?: string; dsp?: string } }>;
}

function TerritoryTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div
      className="px-3 py-2 rounded-lg shadow-lg"
      style={{ background: 'var(--card-elevated)', border: '1px solid var(--border-light)', color: 'var(--foreground)' }}
    >
      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>{d.payload.territory}</p>
      <p className="text-sm font-bold font-mono-data" style={{ color: 'var(--primary-light)' }}>
        {Number(d.value).toLocaleString('en-IN')} streams
      </p>
    </div>
  );
}

export default function AudienceDemographics({ territories, dspPerformance }: AudienceDemographicsProps) {
  const totalStreams = territories.reduce((s, t) => s + t.streams, 0);
  const topTerritories = territories.slice(0, 8);

  const pieData = topTerritories.map((t) => ({
    territory: t.territory,
    value: t.streams,
    pct: totalStreams > 0 ? Math.round((t.streams / totalStreams) * 100) : 0,
  }));

  const dspBarData = dspPerformance
    .sort((a, b) => b.streams - a.streams)
    .slice(0, 6)
    .map((d) => ({
      ...d,
      color: DSP_COLORS[d.dsp] || '#888',
    }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Territory Pie */}
      <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--foreground)' }}>Audience by Territory</h3>
        <p className="text-xs mb-5" style={{ color: 'var(--muted-foreground)' }}>Stream distribution across regions</p>

        {territories.length === 0 ? (
          <div className="flex items-center justify-center h-48" style={{ color: 'var(--muted-foreground)' }}>
            <p className="text-sm">No territory data yet</p>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={180}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={TERRITORY_COLORS[i % TERRITORY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<TerritoryTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <div className="flex-1 space-y-2">
              {pieData.slice(0, 6).map((t, i) => (
                <div key={t.territory} className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: TERRITORY_COLORS[i % TERRITORY_COLORS.length] }}
                  />
                  <span className="text-xs flex-1 truncate" style={{ color: 'var(--secondary-foreground)' }}>{t.territory}</span>
                  <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--foreground)' }}>{t.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* DSP Performance Bar */}
      <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--foreground)' }}>DSP Performance</h3>
        <p className="text-xs mb-5" style={{ color: 'var(--muted-foreground)' }}>Streams per platform</p>

        {dspBarData.length === 0 ? (
          <div className="flex items-center justify-center h-48" style={{ color: 'var(--muted-foreground)' }}>
            <p className="text-sm">No DSP data yet</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={dspBarData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="dsp"
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={40}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(value: number) => [Number(value).toLocaleString('en-IN'), 'Streams']}
                contentStyle={{
                  background: 'var(--card-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  color: 'var(--foreground)',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey="streams" radius={[4, 4, 0, 0]} maxBarSize={32}>
                {dspBarData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
